import React, { useEffect, useState } from 'react';
import GGApi from '../../utils/GGApi.js';
import { buildSmsHref } from '../../utils/gifting_page_share';
import { logGgPlusPassShareLinkCopied } from '../../utils/ggPlusAnalytics.js';
import passMark from '../../images/illustrations/gg+ pass 100.png';
import '../receive_components/Received_gift/Received_gift.css';
import '../pages/Sent_gift_page/Sent_gift_page.css';
import './Gg_plus_shared.css';

function passSpan(pass) {
  const months = Number(pass && pass.durationMonths) || 3;
  return months === 1 ? '1 month' : `${months} months`;
}

function sharePassMessage(pass) {
  return `I have a pass for you. It's ${passSpan(pass)} of GG+, and no card is required.`;
}

function sharePassMailto(pass) {
  const body = `${sharePassMessage(pass)}\n\n${pass.publicUrl}`;
  return `mailto:?subject=${encodeURIComponent('A GG+ Pass for you')}&body=${encodeURIComponent(body)}`;
}

function passAnalytics(pass) {
  return {
    pass_id: pass.passId,
    source_type: pass.sourceType,
    source_id: pass.sourceId,
    referrer_user_id: pass.referrerUserId,
    duration_months: pass.durationMonths,
  };
}

function sectionCopy(passes) {
  const count = passes.length;
  const span = passSpan(passes[0]);
  const countLine = count === 1
    ? 'You have 1 pass to share.'
    : `You have ${count} passes to share.`;
  const each = count === 1 ? 'It gives' : 'Each gives';
  return {
    countLine,
    detailLine: `${each} one person ${span} of GG+ at no cost.`,
  };
}

/**
 * The member's own GG+ passes. Each card is one single-use link.
 * Sharing does not claim it. A redemption does.
 */
function Gg_plus_share_passes() {
  const [passes, setPasses] = useState([]);
  const [copyFailedPassId, setCopyFailedPassId] = useState(null);
  const [canNativeShare] = useState(() => typeof navigator !== 'undefined' && typeof navigator.share === 'function');

  useEffect(() => {
    let cancelled = false;
    // Member share links are created elsewhere; this only reads them.
    GGApi.list_member_share_passes()
      .then((res) => {
        if (!cancelled) setPasses((res && res.passes) || []);
      })
      .catch(() => {
        if (!cancelled) setPasses([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function copyShareLink(pass) {
    const url = pass && pass.publicUrl;
    if (!url) return;
    const finish = () => {
      setCopyFailedPassId(null);
      logGgPlusPassShareLinkCopied(passAnalytics(pass));
      alert('Pass link copied');
    };
    const showUrl = () => setCopyFailedPassId(pass.passId);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(finish).catch(showUrl);
      return;
    }
    // Older browsers: select a temporary field and copy from there.
    const field = document.createElement('textarea');
    field.value = url;
    document.body.appendChild(field);
    field.select();
    try {
      if (document.execCommand('copy')) finish();
      else showUrl();
    } catch (err) {
      showUrl();
    }
    document.body.removeChild(field);
  }

  async function sharePass(pass) {
    if (!canNativeShare || !pass || !pass.publicUrl) {
      copyShareLink(pass);
      return;
    }
    try {
      await navigator.share({
        title: 'A GG+ Pass for you',
        text: sharePassMessage(pass),
        url: pass.publicUrl,
      });
    } catch (err) {
      // Closing the share sheet leaves the pass available.
      if (err && err.name === 'AbortError') return;
      copyShareLink(pass);
    }
  }

  if (!passes.length) return null;

  const intro = sectionCopy(passes);

  return (
    <div className="card membership_pass_section">
      {/* Section header. The passes sit in a padded well under this. */}
      <div className="membership_pass_heading">
        <img src={passMark} alt="" className="membership_pass_mark" />
        <div className="membership_pass_heading_copy">
          <h2 className="membership_details_heading">Your GG+ Passes</h2>
          <p>{intro.countLine}</p>
          <p>{intro.detailLine}</p>
        </div>
      </div>
      <div className="membership_pass_list">
        {passes.map((pass, index) => {
          const claimed = !!pass.claimed;
          const span = passSpan(pass);
          return (
            <div
              key={pass.passId || index}
              className={claimed ? 'card membership_pass_card membership_pass_card_claimed' : 'card membership_pass_card'}
            >
              <div className="membership_pass_head">
                <p className="membership_pass_label">Pass {index + 1}</p>
                <span className={claimed
                  ? 'sent_gift_page_badge delivered membership_pass_status'
                  : 'monthly_gift_badge membership_pass_status'}
                >
                  {claimed ? 'CLAIMED' : 'AVAILABLE'}
                </span>
              </div>
              <p className="membership_pass_title">{claimed ? `${span} of GG+ claimed` : `Give ${span} of GG+`}</p>
              {claimed && pass.claimerFirstName ? (
                <p className="membership_pass_note">Claimed by {pass.claimerFirstName}</p>
              ) : null}
              {!claimed ? (
                <p className="membership_pass_note">One person · No credit card required</p>
              ) : null}
              {!claimed && copyFailedPassId === pass.passId ? (
                <p className="membership_pass_url">{pass.publicUrl}</p>
              ) : null}
              {!claimed ? (
                <div className="membership_pass_actions">
                  <a className="membership_pass_action" href={buildSmsHref(sharePassMessage(pass), pass.publicUrl)}>
                    <i className="material-icons-outlined">textsms</i>
                    <span>Text</span>
                  </a>
                  <a className="membership_pass_action" href={sharePassMailto(pass)}>
                    <i className="material-icons-outlined">email</i>
                    <span>Email</span>
                  </a>
                  <button type="button" className="membership_pass_action" onClick={() => sharePass(pass)}>
                    <i className="material-icons-outlined">{canNativeShare ? 'share' : 'link'}</i>
                    <span>{canNativeShare ? 'Share' : 'Copy link'}</span>
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Gg_plus_share_passes;
